import { useRef, useState, useCallback, useEffect } from 'react';

// ── ICE / TURN Configuration ────────────────────────────────────────────────
// TURN servers are REQUIRED for calls between users behind different NATs.
// Without TURN, calls only work on the same local network.
//
// These use the free Metered TURN relay — replace with your own credentials
// from https://www.metered.ca/tools/openrelay/ for better reliability.
const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    // TURN over UDP (fastest)
    { urls: 'turn:openrelay.metered.ca:80',   username: 'openrelayproject', credential: 'openrelayproject' },
    // TURN over TCP (fallback through strict firewalls)
    { urls: 'turn:openrelay.metered.ca:443',  username: 'openrelayproject', credential: 'openrelayproject' },
    // TURN over TLS (most reliable through HTTPS proxies)
    { urls: 'turns:openrelay.metered.ca:443', username: 'openrelayproject', credential: 'openrelayproject' },
  ],
  iceCandidatePoolSize: 10,
};

function getWs(ws) {
  return ws && typeof ws === 'object' && 'current' in ws ? ws.current : ws;
}

// Boost Opus audio quality and enable Forward Error Correction
function setMediaBitrate(sdp) {
  return sdp.replace(
    /a=fmtp:111 minptime=10;useinbandfec=1/g,
    'a=fmtp:111 minptime=10;useinbandfec=1;stereo=1;sprop-stereo=1;maxaveragebitrate=128000'
  );
}

export function useWebRTC(ws, onCallEnd) {
  const [callState, setCallState]   = useState('idle');
  const [callType, setCallType]     = useState(null);
  const [isMuted, setIsMuted]       = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callerName, setCallerName] = useState('');

  const pcRef           = useRef(null);
  const partnerIdRef    = useRef(null);
  const localStreamRef  = useRef(null);
  const remoteStreamRef = useRef(null);
  const localVideoRef   = useRef(null);
  const remoteVideoRef  = useRef(null);
  const callStateRef    = useRef('idle');
  const pendingOfferRef = useRef(null);

  const updateCallState = useCallback((s) => {
    callStateRef.current = s;
    setCallState(s);
  }, []);

  // ── Get local camera/mic ────────────────────────────────────────────────
  const getLocalMedia = useCallback(async (type) => {
    const constraints = type === 'video'
      ? { audio: true, video: { facingMode: 'user' } }
      : { audio: true, video: false };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      return stream;
    } catch (err) {
      const msg = err.name === 'NotReadableError'
        ? 'Microphone/camera already in use by another app or tab.'
        : err.name === 'NotAllowedError'
        ? 'Permission denied. Please allow microphone/camera access in browser settings.'
        : `Could not access microphone/camera: ${err.message}`;
      alert(msg);
      return null;
    }
  }, []);

  const cleanupMedia = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    remoteStreamRef.current = null;
    if (localVideoRef.current)  localVideoRef.current.srcObject  = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
  }, []);

  const closePc = useCallback(() => {
    if (pcRef.current) {
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.onicecandidate          = null;
      pcRef.current.ontrack                 = null;
      pcRef.current.onnegotiationneeded     = null;
      try { pcRef.current.close(); } catch {}
      pcRef.current = null;
    }
  }, []);

  const endCall = useCallback((sendSignal = true) => {
    closePc();
    cleanupMedia();
    if (sendSignal && callStateRef.current !== 'idle') {
      const socket = getWs(ws);
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'call_end', target_id: partnerIdRef.current }));
      }
    }
    updateCallState('idle');
    setCallType(null);
    setCallerName('');
    setIsMuted(false);
    setIsVideoOff(false);
    pendingOfferRef.current = null;
    partnerIdRef.current    = null;
    onCallEnd?.();
  }, [ws, closePc, cleanupMedia, updateCallState, onCallEnd]);

  // ── Create RTCPeerConnection ─────────────────────────────────────────────
  const createPeerConnection = useCallback(() => {
    closePc();
    const pc = new RTCPeerConnection(ICE_SERVERS);
    pcRef.current = pc;

    pc.onicecandidate = (e) => {
      const socket = getWs(ws);
      if (e.candidate && socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({
          type: 'webrtc_ice_candidate',
          candidate: e.candidate,
          target_id: partnerIdRef.current,
        }));
      }
    };

    pc.ontrack = (e) => {
      const stream = e.streams[0] || new MediaStream([e.track]);
      remoteStreamRef.current = stream;
      // Attach immediately to video element if available
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
        // For audio calls, force play (browsers block autoplay without user gesture)
        remoteVideoRef.current.play().catch(() => {});
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc !== pcRef.current) return;
      if (pc.connectionState === 'failed') {
        console.warn('[WebRTC] connection failed — trying ICE restart');
        // ICE restart: renegotiate with new candidates
        if (callStateRef.current === 'active') {
          pc.restartIce();
        }
      }
      if (pc.connectionState === 'disconnected') {
        console.warn('[WebRTC] peer disconnected');
      }
    };

    pc.oniceconnectionstatechange = () => {
      if (pc !== pcRef.current) return;
      if (pc.iceConnectionState === 'failed') {
        pc.restartIce();
      }
    };

    return pc;
  }, [ws, closePc]);

  // ── Start outgoing call ─────────────────────────────────────────────────
  const startCall = useCallback(async (type, targetUserId) => {
    if (!targetUserId) { alert('No call target specified.'); return; }
    updateCallState('calling');
    setCallType(type);
    setIsMuted(false);
    setIsVideoOff(false);
    partnerIdRef.current = targetUserId;

    const stream = await getLocalMedia(type);
    if (!stream) { updateCallState('idle'); setCallType(null); partnerIdRef.current = null; return; }

    const pc = createPeerConnection();
    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    try {
      let offer = await pc.createOffer(
        type === 'video'
          ? { offerToReceiveVideo: true, offerToReceiveAudio: true }
          : { offerToReceiveAudio: true, offerToReceiveVideo: false }
      );
      offer = { type: 'offer', sdp: setMediaBitrate(offer.sdp) };
      await pc.setLocalDescription(offer);

      const socket = getWs(ws);
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'call_request', callType: type, offer, target_id: targetUserId }));
      } else {
        alert('Not connected to server. Please refresh and try again.');
        closePc(); cleanupMedia(); updateCallState('idle'); setCallType(null); partnerIdRef.current = null;
      }
    } catch (err) {
      console.error('[WebRTC] startCall error:', err);
      alert('Failed to create call offer: ' + err.message);
      closePc(); cleanupMedia(); updateCallState('idle'); setCallType(null); partnerIdRef.current = null;
    }
  }, [ws, getLocalMedia, createPeerConnection, closePc, cleanupMedia, updateCallState]);

  // ── Accept incoming call ─────────────────────────────────────────────────
  const acceptCall = useCallback(async (offer, type) => {
    if (!offer) { console.warn('[WebRTC] acceptCall: no pending offer'); return; }
    setIsMuted(false);
    setIsVideoOff(false);

    const stream = await getLocalMedia(type);
    if (!stream) { updateCallState('idle'); setCallType(null); return; }

    const pc = createPeerConnection();
    stream.getTracks().forEach(track => pc.addTrack(track, stream));

    try {
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      let answer = await pc.createAnswer();
      answer = { type: 'answer', sdp: setMediaBitrate(answer.sdp) };
      await pc.setLocalDescription(answer);
      updateCallState('active');

      const socket = getWs(ws);
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'call_accept', answer, target_id: partnerIdRef.current }));
      }
    } catch (err) {
      console.error('[WebRTC] acceptCall error:', err);
      alert('Failed to accept call: ' + err.message);
      closePc(); cleanupMedia(); updateCallState('idle');
    }
  }, [ws, getLocalMedia, createPeerConnection, closePc, cleanupMedia, updateCallState]);

  const rejectCall = useCallback(() => {
    const socket = getWs(ws);
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'call_reject', target_id: partnerIdRef.current }));
    }
    closePc(); cleanupMedia(); updateCallState('idle');
    setCallerName('');
    pendingOfferRef.current = null;
    partnerIdRef.current    = null;
  }, [ws, closePc, cleanupMedia, updateCallState]);

  // ── Handle incoming WebRTC signals ──────────────────────────────────────
  const handleSignal = useCallback(async (data) => {
    switch (data.type) {
      case 'call_request':
        if (callStateRef.current === 'active') {
          // Already in a call — auto-reject
          const socket = getWs(ws);
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'call_reject', target_id: data.sender_id }));
          }
          break;
        }
        setCallerName(data.sender_username || 'User');
        setCallType(data.callType);
        partnerIdRef.current    = data.sender_id;
        pendingOfferRef.current = data.offer;
        updateCallState('receiving');
        break;

      case 'call_accept': {
        const pc = pcRef.current;
        if (!pc || !data.answer) break;
        if (pc.signalingState !== 'have-local-offer') {
          console.warn('[WebRTC] call_accept ignored — wrong signalingState:', pc.signalingState);
          break;
        }
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
          if (pcRef.current === pc) updateCallState('active');
        } catch (err) {
          console.warn('[WebRTC] call_accept setRemoteDescription failed:', err.message);
        }
        break;
      }

      case 'call_reject':
        alert('Call was declined.');
        endCall(false);
        break;

      case 'call_end':
        closePc(); cleanupMedia(); updateCallState('idle');
        setCallType(null); setCallerName('');
        setIsMuted(false); setIsVideoOff(false);
        pendingOfferRef.current = null;
        partnerIdRef.current    = null;
        break;

      case 'webrtc_offer': {
        const pc = pcRef.current;
        if (!pc || !data.offer) break;
        try {
          if (pc.signalingState !== 'stable') break;
          await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          const socket = getWs(ws);
          if (socket?.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'webrtc_answer', answer, target_id: partnerIdRef.current }));
          }
        } catch (err) { console.warn('[WebRTC] webrtc_offer error:', err); }
        break;
      }

      case 'webrtc_answer': {
        const pc = pcRef.current;
        if (!pc || !data.answer) break;
        try {
          if (pc.signalingState !== 'have-local-offer') break;
          await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
          if (pcRef.current === pc) updateCallState('active');
        } catch (err) { console.warn('[WebRTC] webrtc_answer error:', err); }
        break;
      }

      case 'webrtc_ice_candidate': {
        const pc = pcRef.current;
        if (!pc || !data.candidate) break;
        try { await pc.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch {}
        break;
      }

      default: break;
    }
  }, [ws, endCall, closePc, cleanupMedia, updateCallState]);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const enabled = isMuted; // if currently muted, we want to enable
      localStreamRef.current.getAudioTracks().forEach(t => { t.enabled = enabled; });
      setIsMuted(m => !m);
    }
  }, [isMuted]);

  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const enabled = isVideoOff; // if currently off, we want to enable
      localStreamRef.current.getVideoTracks().forEach(t => { t.enabled = enabled; });
      setIsVideoOff(v => !v);
    }
  }, [isVideoOff]);

  const acceptPendingCall = useCallback(() => {
    acceptCall(pendingOfferRef.current, callType);
  }, [acceptCall, callType]);

  // ── Sync streams to video elements whenever callState changes ───────────
  useEffect(() => {
    const sync = () => {
      if (localVideoRef.current && localStreamRef.current
          && localVideoRef.current.srcObject !== localStreamRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
      if (remoteVideoRef.current && remoteStreamRef.current
          && remoteVideoRef.current.srcObject !== remoteStreamRef.current) {
        remoteVideoRef.current.srcObject = remoteStreamRef.current;
        remoteVideoRef.current.play().catch(() => {});
      }
    };
    sync();
    const id = setInterval(sync, 500);
    return () => clearInterval(id);
  }, [callState, callType]);

  return {
    callState, callType, isMuted, isVideoOff, callerName,
    localVideoRef, remoteVideoRef,
    startCall, acceptPendingCall, rejectCall, endCall,
    handleSignal, toggleMute, toggleVideo,
  };
}
